import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { checkCashRegister } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  Receipt,
  Package,
  Users,
  FileText,
  FileCheck,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  X,
  UserCog,
  History,
  Building2,
  FileSearch,
  Warehouse,
  Scissors,
  Landmark,
  Wrench,
  Plus,
  BookOpen,
} from 'lucide-react';


const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1024);
  const [profileOpen, setProfileOpen] = useState(false);

  // Cerrar sidebar automáticamente en mobile al navegar
  useEffect(() => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
     { path: '/customers', label: 'Clientes', icon: Users },
    { path: '/pos', label: 'Facturar', icon: Receipt },
    { path: '/tailoring-orders', label: 'Pedidos Confección', icon: Scissors, newPath: '/tailoring-orders/new' },
    { path: '/service-orders', label: 'Orden Servicios', icon: Wrench, newPath: '/service-orders/new' },
    { path: '/cash-register', label: 'Operaciones Caja', icon: Landmark },
    { path: '/products', label: 'Productos', icon: Package },
    { path: '/invoices', label: 'Listado Ventas', icon: FileSearch },
    { path: '/quotations', label: 'Cotizaciones', icon: FileText },
    { path: '/branch-inventory', label: 'Inventario Sucursal', icon: Warehouse },
    { path: '/inventory-history', label: 'Historial Inventario', icon: History },
    { path: '/credit-notes', label: 'Notas de Crédito', icon: CreditCard },
    { path: '/reports', label: 'Reportes', icon: FileCheck },
    { path: '/branches', label: 'Sucursales', icon: Building2, adminOnly: true },
    { path: '/users', label: 'Usuarios', icon: UserCog, adminOnly: true },
    { path: '/settings', label: 'Configuración', icon: Settings },
    { path: '/manual', label: 'Manual de Usuario', icon: BookOpen },
  ];

  // Filtrar menú según rol
  const filteredMenu = menuItems.filter(item => {
    if (item.adminOnly && user?.role !== 'admin') return false;
    return true;
  });

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden" data-testid="layout-container">

      {/* Backdrop mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-40 flex flex-col
          lg:relative lg:inset-auto lg:z-auto lg:flex-shrink-0
          ${sidebarOpen
            ? 'w-64 translate-x-0'
            : 'w-64 -translate-x-full lg:translate-x-0 lg:w-20'}
          bg-slate-900 text-white transition-all duration-300
        `}
        data-testid="sidebar"
      >
        <div className="p-4 flex items-center justify-between border-b border-slate-800">
          {sidebarOpen && (
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit' }} data-testid="sidebar-title">
              Batas Médicas RD
            </h1>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:bg-slate-800"
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto" data-testid="sidebar-nav">
          {filteredMenu.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <div key={item.path} className="flex items-center gap-1">
                <Link
                  to={item.path}
                  className={`flex-1 flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-cyan-500 text-white shadow-md'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                  data-testid={`nav-link-${item.path.substring(1)}`}
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  {sidebarOpen && <span className="font-medium">{item.label}</span>}
                </Link>
                {sidebarOpen && item.newPath && (
                  <button
                    title={item.newLabel || 'Nuevo'}
                    onClick={async () => {
                      try {
                        const res = await checkCashRegister();
                        const open = !res.required || res.open;
                        if (!open) {
                          toast.warning('Abre un turno de caja para crear registros');
                          return;
                        }
                      } catch {
                        // si falla la verificación, dejar pasar
                      }
                      navigate(item.newPath);
                    }}
                    className="w-6 h-6 flex items-center justify-center rounded-full border border-slate-500 text-slate-400 hover:border-white hover:text-white transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800 relative">
          {/* Popover perfil */}
          {profileOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />
              <div className="absolute bottom-full left-4 right-4 mb-2 z-50 bg-white rounded-xl shadow-2xl border border-slate-200 p-4 text-slate-800">
                <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-100">
                  <div className="w-10 h-10 bg-cyan-500 rounded-full flex items-center justify-center font-bold text-white shrink-0">
                    {user?.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{user?.name}</p>
                    <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  {user?.email && (
                    <div>
                      <p className="text-xs text-slate-400">Correo</p>
                      <p className="font-medium truncate">{user.email}</p>
                    </div>
                  )}
                  {user?.branch_name && (
                    <div>
                      <p className="text-xs text-slate-400">Sucursal</p>
                      <p className="font-medium">{user.branch_name}</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <button
            onClick={() => setProfileOpen(o => !o)}
            className={`${sidebarOpen ? 'flex items-center space-x-3' : 'flex justify-center'} mb-3 w-full rounded-lg hover:bg-slate-800 p-1 transition-colors`}
          >
            <div className="w-10 h-10 bg-cyan-500 rounded-full flex items-center justify-center font-bold shrink-0">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            {sidebarOpen && (
              <div className="text-left min-w-0">
                <p className="font-medium truncate" data-testid="user-name">{user?.name}</p>
                <p className="text-xs text-slate-400" data-testid="user-role">{user?.role}</p>
              </div>
            )}
          </button>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className={`${sidebarOpen ? 'w-full' : 'w-10'} text-slate-300 hover:bg-slate-800 hover:text-white`}
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="ml-2">Cerrar Sesión</span>}
          </Button>
          {sidebarOpen && (
            <p className="text-center text-slate-600 text-xs mt-2">
              v{process.env.REACT_APP_VERSION}
            </p>
          )}
        </div>
      </aside>

      {/* Contenido principal */}
      <div className="flex-1 flex flex-col min-h-0 min-w-0">
        {/* Barra superior mobile */}
        <header className="lg:hidden flex items-center gap-3 px-4 py-3 bg-slate-900 text-white flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1 rounded text-white hover:bg-slate-800 transition-colors"
            aria-label="Abrir menú"
          >
            <Menu className="w-5 h-5" />
          </button>
          <span className="font-bold text-lg" style={{ fontFamily: 'Outfit' }}>Batas Médicas RD</span>
        </header>

        <main className="flex-1 overflow-auto min-h-0" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
